import { Controller } from "@hotwired/stimulus";
import { stringifyParams } from "./helpers/utils";

const RANSACK_PREDICATES = {
    eq: 'equals',
    eq_any: 'equals any',
    eq_all: 'equals all',
    not_eq: 'does not equal',
    not_eq_any: 'does not equal any',
    not_eq_all: 'does not equal all',
    matches: 'matches (regexp)',
    matches_any: 'matches any (regexp)',
    matches_all: 'matches all (regexp)',
    does_not_match: 'does not match (regexp)',
    does_not_match_any: 'does not match any (regexp)',
    does_not_match_all: 'does not match all (regexp)',
    lt: 'less than',
    lt_any: 'less than any',
    lt_all: 'less than all',
    lteq: 'less than or equal to',
    lteq_any: 'less than or equal to any',
    lteq_all: 'less than or equal to all',
    gt: 'greater than',
    gt_any: 'greater than any',
    gt_all: 'greater than all',
    gteq: 'greater than or equal to',
    gteq_any: 'greater than or equal to any',
    gteq_all: 'greater than or equal to all',
    in: 'in',
    in_any: 'in any',
    in_all: 'in all',
    not_in: 'not in',
    not_in_any: 'not in any',
    not_in_all: 'not in all',
    cont: 'contains',
    cont_any: 'contains any',
    cont_all: 'contains all',
    not_cont: 'does not contain',
    not_cont_any: 'does not contain any',
    not_cont_all: 'does not contain all',
    start: 'starts with',
    start_any: 'starts with any',
    start_all: 'starts with all',
    not_start: 'does not start with',
    not_start_any: 'does not start with any',
    not_start_all: 'does not start with all',
    end: 'ends with',
    end_any: 'ends with any',
    end_all: 'ends with all',
    not_end: 'does not end with',
    not_end_any: 'does not end with any',
    not_end_all: 'does not end with all',
    true: 'is true',
    not_true: 'is not true',
    false: 'is false',
    not_false: 'is not false',
    present: 'is present',
    not_present: 'is not present',
    blank: 'is blank',
    not_blank: 'is not blank',
    i_cont: 'contains (case-insensitive)',
    i_cont_any: 'contains any (case-insensitive)',
    i_cont_all: 'contains all (case-insensitive)',
    not_i_cont: 'does not contain (case-insensitive)',
    not_i_cont_any: 'does not contain any (case-insensitive)',
    not_i_cont_all: 'does not contain all (case-insensitive)',
    i_start: 'starts with (case-insensitive)',
    i_start_any: 'starts with any (case-insensitive)',
    i_start_all: 'starts with all (case-insensitive)',
    not_i_start: 'does not start with (case-insensitive)',
    not_i_start_any: 'does not start with any (case-insensitive)',
    not_i_start_all: 'does not start with all (case-insensitive)',
    i_end: 'ends with (case-insensitive)',
    i_end_any: 'ends with any (case-insensitive)',
    i_end_all: 'ends with all (case-insensitive)',
    not_i_end: 'does not end with (case-insensitive)',
    not_i_end_any: 'does not end with any (case-insensitive)',
    not_i_end_all: 'does not end with all (case-insensitive)',
    i_matches: 'matches (case-insensitive regexp)',
    i_matches_any: 'matches any (case-insensitive regexp)',
    i_matches_all: 'matches all (case-insensitive regexp)',
    not_i_matches: 'does not match (case-insensitive regexp)',
    not_i_matches_any: 'does not match any (case-insensitive regexp)',
    not_i_matches_all: 'does not match all (case-insensitive regexp)',
    i_eq: 'equals (case-insensitive)',
    i_eq_any: 'equals any (case-insensitive)',
    i_eq_all: 'equals all (case-insensitive)',
    not_i_eq: 'does not equal (case-insensitive)',
    not_i_eq_any: 'does not equal any (case-insensitive)',
    not_i_eq_all: 'does not equal all (case-insensitive)',
    i_not_eq: 'is not equal (case-insensitive)',
    i_not_eq_any: 'is not equal any (case-insensitive)',
    i_not_eq_all: 'is not equal all (case-insensitive)',
    i_in: 'in (case-insensitive)',
    i_in_any: 'in any (case-insensitive)',
    i_in_all: 'in all (case-insensitive)',
    i_not_in: 'not in (case-insensitive)',
    i_not_in_any: 'not in any (case-insensitive)',
    i_not_in_all: 'not in all (case-insensitive)',
    date_equals: 'date equals',
    date_equals_any: 'date equals any',
    date_equals_all: 'date equals all',
    date_not_equals: 'date does not equal',
    date_not_equals_any: 'date does not equal any',
    date_not_equals_all: 'date does not equal all',
    date_gteq: 'date greater than or equal to',
    date_gteq_any: 'date greater than or equal to any',
    date_gteq_all: 'date greater than or equal to all',
    date_lteq: 'date less than or equal to',
    date_lteq_any: 'date less than or equal to any',
    date_lteq_all: 'date less than or equal to all',
    date_gt: 'date greater than',
    date_gt_any: 'date greater than any',
    date_gt_all: 'date greater than all',
    date_lt: 'date less than',
    date_lt_any: 'date less than any',
    date_lt_all: 'date less than all',
    date_between: 'date between',
    date_between_any: 'date between any',
    date_between_all: 'date between all',
    date_not_between: 'date not between',
    date_not_between_any: 'date not between any',
    date_not_between_all: 'date not between all',
    i_cont_jem: 'contains JEM',
    descendant_status_eq: 'Descendant Status Equals',
    any_level_status_eq: 'Any Level Status Equals',
    descendant_status_not_eq: 'Descendant Status Not Equal',
    any_level_status_not_eq: 'Any Level Status Not Equal',
};

function splitColumnAndPredicate(key: string, predicates: string[]): { column: string, predicate: string } {
    // Sort predicates by length descending for longest match first
    const sortedPreds = predicates.slice().sort((a, b) => b.length - a.length);
    for (const pred of sortedPreds) {
        if (key.endsWith('_' + pred)) {
            return {
                column: key.slice(0, -(pred.length + 1)),
                predicate: pred
            };
        }
    }
    // fallback: treat everything before last _ as column, after as predicate
    const lastUnderscore = key.lastIndexOf('_');
    if (lastUnderscore !== -1) {
        return {
            column: key.slice(0, lastUnderscore),
            predicate: key.slice(lastUnderscore + 1)
        };
    }
    return { column: key, predicate: '' };
}

export default class extends Controller {
    static targets = [
        'filtersContainer',
        'filterRow',
        'search',
        'searchDropdown',
        'searchColumn',
        'searchPredicate',
        'searchValue',
        'applyButton',
        'clearButton',
        'addFilterButton',
        'removeFilterButton',
        'modal',
        'showFilterButton',
        'filterCountBadge'
    ];

    declare filtersContainerTarget: HTMLElement;
    declare modalTarget: HTMLElement;
    declare showFilterButtonTarget: HTMLElement;
    declare applyButtonTarget: HTMLElement;
    declare clearButtonTarget: HTMLElement;
    declare filterCountBadgeTarget: HTMLElement;
    declare hasModalTarget: boolean;
    declare hasFilterCountBadgeTarget: boolean;
    declare columnsValue: any[];

    static values = {
        columns: Array
    };

    async connect() {
        this.cleanupExtraFilterRows();
        this.updateRemoveButtonsVisibility();
        this.populateColumnOptions();

        // Prefill filters from URL params (now supports multiple rows, async)
        await this.prefillFiltersFromUrlParams();

        // After all filter UI setup, update badge
        this.updateFilterCountBadge();

        // Add keyboard shortcuts for modal
        if (this.hasModalTarget) {
            this.modalTarget.addEventListener('keydown', (event: KeyboardEvent) => {
                if (event.key === 'Enter') {
                    event.preventDefault();
                    this.applySearch();
                } else if (event.key === 'Escape') {
                    event.preventDefault();
                    this.closeModal();
                }
            });
        }
    }

    cleanupExtraFilterRows() {
        const rows = this.filtersContainerTarget.querySelectorAll('[data-jsonapi-filter-target="filterRow"]');
        rows.forEach((row, idx) => { if (idx > 0) (row as HTMLElement).remove(); });
    }

    addFilterRow(event) {
        event.preventDefault();
        const firstRow = this.filtersContainerTarget.querySelector('[data-jsonapi-filter-target="filterRow"]') as HTMLElement;
        if (!firstRow) return;
        const newRow = firstRow.cloneNode(true) as HTMLElement;
        newRow.querySelectorAll('select, input').forEach((el) => { (el as HTMLInputElement | HTMLSelectElement).value = ''; });
        (newRow.querySelector('[data-jsonapi-filter-target="searchValue"]') as HTMLElement).classList.add('hidden');
        (newRow.querySelector('[data-jsonapi-filter-target="searchPredicate"]') as HTMLElement).classList.add('hidden');
        this.filtersContainerTarget.appendChild(newRow);
        this.updateRemoveButtonsVisibility();
        this.populateColumnOptions();
    }

    removeFilterRow(event) {
        event.preventDefault();
        const row = event.target.closest('[data-jsonapi-filter-target="filterRow"]');
        if (row && this.filtersContainerTarget.querySelectorAll('[data-jsonapi-filter-target="filterRow"]').length > 1) {
            row.remove();
            this.updateRemoveButtonsVisibility();
        }
    }

    updateRemoveButtonsVisibility() {
        const rows = this.filtersContainerTarget.querySelectorAll('[data-jsonapi-filter-target="filterRow"]');
        const show = rows.length > 1;
        rows.forEach(row => {
            const btn = row.querySelector('[data-jsonapi-filter-target="removeFilterButton"]');
            if (btn) {
                if (show) {
                    btn.classList.remove('hidden');
                } else {
                    btn.classList.add('hidden');
                }
            }
        });
    }

    populateColumnOptions() {
        const rows = this.filtersContainerTarget.querySelectorAll('[data-jsonapi-filter-target="filterRow"]');
        rows.forEach(row => {
            const select = row.querySelector('[data-jsonapi-filter-target="searchColumn"]') as HTMLSelectElement;
            if (!select) return;
            if (select.options.length <= 1) {
                select.innerHTML = '<option value="">Search By Column</option>';
                (this.columnsValue || []).forEach(col => {
                    const option = document.createElement('option');
                    option.value = col.value;
                    option.text = col.label;
                    select.appendChild(option);
                });
            }
        });
    }

    updatePredicates(event) {
        const row = (event.target as HTMLElement).closest('[data-jsonapi-filter-target="filterRow"]');
        if (!row) return;
        const column = (row.querySelector('[data-jsonapi-filter-target="searchColumn"]') as HTMLSelectElement).value;
        const predicateSelect = row.querySelector('[data-jsonapi-filter-target="searchPredicate"]') as HTMLSelectElement;
        predicateSelect.innerHTML = '';
        const defaultOption = document.createElement('option');
        defaultOption.value = '';
        defaultOption.text = 'Select Predicate';
        predicateSelect.appendChild(defaultOption);
        const col = (this.columnsValue || []).find(c => c.value === column);
        if (col) {
            col.predicates.forEach(predicate => {
                const option = document.createElement('option');
                option.value = predicate;
                option.text = RANSACK_PREDICATES[predicate] || predicate;
                predicateSelect.appendChild(option);
            });
        }
        this.togglePredicateInputRow(row as HTMLElement);
        predicateSelect.value = '';
        (row.querySelector('[data-jsonapi-filter-target="search"]') as HTMLInputElement).value = '';
        (row.querySelector('[data-jsonapi-filter-target="searchValue"]') as HTMLElement).classList.add('hidden');
    }

    shouldUseDropdown(col: any, predicate: string): boolean {
        if (!col || !predicate) return false;
        const dropdownPredicates = Array.isArray(col.dropdown_predicates) ? col.dropdown_predicates : [];
        return Array.isArray(col.dropdown_options) && col.dropdown_options.length > 0 && dropdownPredicates.includes(predicate);
    }

    toggleSearchInput(event) {
        const row = (event.target as HTMLElement).closest('[data-jsonapi-filter-target="filterRow"]');
        if (!row) return;
        const predicate = (row.querySelector('[data-jsonapi-filter-target="searchPredicate"]') as HTMLSelectElement).value;
        const searchValueDiv = row.querySelector('[data-jsonapi-filter-target="searchValue"]') as HTMLElement;
        const column = (row.querySelector('[data-jsonapi-filter-target="searchColumn"]') as HTMLSelectElement).value;
        const col = (this.columnsValue || []).find(c => c.value === column);
        // Hide both inputs by default
        const textInput = row.querySelector('[data-jsonapi-filter-target="search"]') as HTMLInputElement;
        const dropdownInput = row.querySelector('[data-jsonapi-filter-target="searchDropdown"]') as HTMLSelectElement;
        if (textInput) textInput.classList.add('hidden');
        if (dropdownInput) dropdownInput.classList.add('hidden');
        if (predicate) {
            searchValueDiv.classList.remove('hidden');
            if (this.shouldUseDropdown(col, predicate)) {
                // Show dropdown, populate options only for allowed predicates
                if (dropdownInput) {
                    dropdownInput.innerHTML = '<option value="">Select...</option>';
                    col.dropdown_options.forEach(opt => {
                        const option = document.createElement('option');
                        option.value = opt.value;
                        option.text = opt.label;
                        dropdownInput.appendChild(option);
                    });
                    dropdownInput.classList.remove('hidden');
                }
            } else {
                if (textInput) textInput.classList.remove('hidden');
            }
        } else {
            searchValueDiv.classList.add('hidden');
        }
        // Clear both inputs
        if (textInput) textInput.value = '';
        if (dropdownInput) dropdownInput.value = '';
    }

    togglePredicateInputRow(row: HTMLElement) {
        const column = (row.querySelector('[data-jsonapi-filter-target="searchColumn"]') as HTMLSelectElement).value;
        const predicateSelect = row.querySelector('[data-jsonapi-filter-target="searchPredicate"]') as HTMLSelectElement;
        const clearButton = this.clearButtonTarget;
        if (column) {
            predicateSelect.classList.remove('hidden');
            clearButton.classList.remove('hidden');
        } else {
            predicateSelect.classList.add('hidden');
        }
        this.toggleSearchInput({ target: predicateSelect });
    }

    getValueInput(row: HTMLElement): HTMLInputElement | HTMLSelectElement | null {
        // Prefer dropdown if visible and predicate is in dropdown_predicates, else text input
        const column = (row.querySelector('[data-jsonapi-filter-target="searchColumn"]') as HTMLSelectElement)?.value;
        const predicate = (row.querySelector('[data-jsonapi-filter-target="searchPredicate"]') as HTMLSelectElement)?.value;
        const col = (this.columnsValue || []).find(c => c.value === column);
        const dropdown = row.querySelector('[data-jsonapi-filter-target="searchDropdown"]') as HTMLSelectElement;
        if (
          dropdown &&
          !dropdown.classList.contains('hidden') &&
          this.shouldUseDropdown(col, predicate)
        ) return dropdown;
        const input = row.querySelector('[data-jsonapi-filter-target="search"]') as HTMLInputElement;
        if (input && !input.classList.contains('hidden')) return input;
        return null;
    }

    buildFilterParams() {
        const filter = {};
        const rows = this.filtersContainerTarget.querySelectorAll('[data-jsonapi-filter-target="filterRow"]');
        rows.forEach(row => {
            const column = (row.querySelector('[data-jsonapi-filter-target="searchColumn"]') as HTMLSelectElement).value;
            const predicate = (row.querySelector('[data-jsonapi-filter-target="searchPredicate"]') as HTMLSelectElement).value;
            const valueInput = this.getValueInput(row as HTMLElement);
            const value = valueInput ? valueInput.value.trim() : '';
            if (column && predicate && value) {
                filter[`${column}_${predicate}`] = value.replace(/\s+/g, ',').toString();
            }
        });
        return filter;
    }

    updateFilterCountBadge() {
        if (!this.hasFilterCountBadgeTarget) return;
        const filter = this.buildFilterParams();
        const count = Object.keys(filter).length;
        if (count > 0) {
            this.filterCountBadgeTarget.textContent = count.toString();
            this.filterCountBadgeTarget.classList.remove('hidden');
        } else {
            this.filterCountBadgeTarget.textContent = '';
            this.filterCountBadgeTarget.classList.add('hidden');
        }
    }

    applySearch() {
        const filter = this.buildFilterParams();
        this.element.dispatchEvent(new CustomEvent('filter:apply', {
            detail: { filter },
            bubbles: true
        }));
        this.closeModal();
        this.updateFilterCountBadge();
    }

    clearSearch() {
        const rows = this.filtersContainerTarget.querySelectorAll('[data-jsonapi-filter-target="filterRow"]');
        rows.forEach((row, idx) => { if (idx > 0) (row as HTMLElement).remove(); });
        const firstRow = this.filtersContainerTarget.querySelector('[data-jsonapi-filter-target="filterRow"]') as HTMLElement;
        if (firstRow) {
            (firstRow.querySelector('[data-jsonapi-filter-target="searchColumn"]') as HTMLSelectElement).value = '';
            (firstRow.querySelector('[data-jsonapi-filter-target="searchPredicate"]') as HTMLSelectElement).value = '';
            (firstRow.querySelector('[data-jsonapi-filter-target="search"]') as HTMLInputElement).value = '';
            (firstRow.querySelector('[data-jsonapi-filter-target="searchValue"]') as HTMLElement).classList.add('hidden');
            (firstRow.querySelector('[data-jsonapi-filter-target="searchPredicate"]') as HTMLElement).classList.add('hidden');
        }
        this.element.dispatchEvent(new CustomEvent('filter:clear', { bubbles: true }));
        this.closeModal();
        this.updateFilterCountBadge();
    }

    openModal() {
        this.modalTarget.classList.remove('hidden');
        // Focus the first input for accessibility
        const firstInput = this.modalTarget.querySelector('input, select, textarea, button');
        if (firstInput) (firstInput as HTMLElement).focus();
    }

    closeModal() {
        this.modalTarget.classList.add('hidden');
    }

    async prefillFiltersFromUrlParams() {
        const urlParams = new URLSearchParams(window.location.search);
        const filterParams: Record<string, string> = {};
        for (const [key, value] of urlParams.entries()) {
            const match = key.match(/^filter\[(.+)\]$/);
            if (match) {
                filterParams[match[1]] = value;
            }
        }
        const filterKeys = Object.keys(filterParams);
        if (filterKeys.length > 0) {
            // Ensure enough filter rows exist
            while (this.filtersContainerTarget.querySelectorAll('[data-jsonapi-filter-target="filterRow"]').length < filterKeys.length) {
                this.addFilterRow(new Event('add'));
            }
            const rows = Array.from(this.filtersContainerTarget.querySelectorAll('[data-jsonapi-filter-target="filterRow"]'));
            for (let idx = 0; idx < filterKeys.length; idx++) {
                const key = filterKeys[idx];
                const { column, predicate } = splitColumnAndPredicate(key, Object.keys(RANSACK_PREDICATES));
                const value = filterParams[key];
                const row = rows[idx];
                if (row) {
                    // Set column
                    const columnSelect = row.querySelector('[data-jsonapi-filter-target="searchColumn"]') as HTMLSelectElement;
                    if (columnSelect) {
                        columnSelect.value = column;
                        columnSelect.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                    // Wait for predicate options to be populated
                    await new Promise(resolve => setTimeout(resolve, 50));
                    const predicateSelect = row.querySelector('[data-jsonapi-filter-target="searchPredicate"]') as HTMLSelectElement;
                    if (predicateSelect) {
                        predicateSelect.value = predicate;
                        predicateSelect.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                    // Wait for value input to be visible
                    await new Promise(resolve => setTimeout(resolve, 30));
                    // Set value (dropdown or text)
                    const col = (this.columnsValue || []).find(c => c.value === column);
                    if (this.shouldUseDropdown(col, predicate)) {
                        const dropdownInput = row.querySelector('[data-jsonapi-filter-target="searchDropdown"]') as HTMLSelectElement;
                        if (dropdownInput) {
                            dropdownInput.value = value;
                            dropdownInput.dispatchEvent(new Event('change', { bubbles: true }));
                        }
                    } else {
                        const valueInput = row.querySelector('[data-jsonapi-filter-target="search"]') as HTMLInputElement;
                        if (valueInput) {
                            valueInput.value = value;
                            valueInput.dispatchEvent(new Event('input', { bubbles: true }));
                        }
                    }
                }
            }
            // After all rows are set, dispatch filter:ready
            this.element.dispatchEvent(new CustomEvent('filter:ready', {
                detail: { filter: this.buildFilterParams() },
                bubbles: true
            }));
        } else {
            // No filter params, dispatch filter:ready
            this.element.dispatchEvent(new CustomEvent('filter:ready', {
                detail: { filter: {} },
                bubbles: true
            }));
        }
    }
} 